import {
  Button,
  HStack,
  Spacer,
  Stack,
  ButtonGroup,
  Box,
  IconButton,
  Icon,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useState, useMemo, ChangeEvent } from "react";
import { t } from "i18next";
import {
  AttributeDefinition,
  AttributeDefinitions,
  AttributeName,
  DistanceUnit,
} from "@keita/lanepatrol-sdk";
import { AttributeUtils } from "../../../../../../coding/utils/attribute-utils";
import { parseAttributeInputValue } from "../../../../../../coding/utils/attribute-value";
import { ForecastActionType } from "../../../../../../project/models/ForecastScenario";
import { AttributeInput } from "../../../../../../shared/views/AttributeInput/AttributeInput";
import { OsaNode } from "../../../../../../osaway/models/OsaNode";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { CloseIcon } from "../../../../../../shared/views/icons";
import { formatDistanceByUnit } from "../../../../../../shared/utils/format-utils";

interface State {
  attribute: AttributeDefinition | undefined;
  value: string | null;
}
interface ValidatedState {
  attribute: AttributeDefinition;
  value: string;
}

interface Props {
  filteredDistance: number;
  filteredNodes: OsaNode[];
  onClose: () => void;
}
const AddAttributeValuesActionBase: React.FC<Props> = ({
  filteredDistance,
  filteredNodes,
  onClose,
}) => {
  const { forecastStore, projectStore } = useStores();
  const distanceUnit = projectStore.distanceUnit;

  const [state, setState] = useState<State[]>([
    {
      attribute: undefined,
      value: null,
    },
  ]);

  const validItems = useMemo(
    () =>
      state.filter(
        ({ attribute, value }) =>
          attribute && parseAttributeInputValue(attribute, value) !== null,
      ) as ValidatedState[],
    [state],
  );

  const onChangeAttributeName =
    (index: number) => (e: ChangeEvent<HTMLSelectElement>) => {
      const attributeName = e.target.value as AttributeName;
      setState((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                attribute: AttributeDefinitions.getByName(attributeName),
                value: null,
              }
            : item,
        ),
      );
    };

  const onChangeAttributeValue = (index: number) => (value: string | null) => {
    setState((prev) =>
      prev.map((item, i) => (i === index ? { ...item, value: value } : item)),
    );
  };

  const onSubmit = () => {
    forecastStore.addChange({
      type: ForecastActionType.AttributeValues,
      attributeValues: Object.fromEntries(
        validItems.map(({ attribute, value }) => [attribute.name, value]),
      ),
    });

    onClose();
  };

  const addItem = () => {
    setState((prev) =>
      prev.concat({
        attribute: undefined,
        value: null,
      }),
    );
  };

  const removeItem = (index: number) => {
    setState((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Stack>
      {state.map(({ attribute, value }, i) => (
        <HStack pb={2} key={i}>
          <NativeSelect.Root flex={1}>
            <NativeSelect.Field
              onChange={onChangeAttributeName(i)}
              value={attribute?.name || ""}
              placeholder={t(
                `map:forecast.AttributeValues.attributeNamePlaceholder`,
              )}
            >
              {AttributeDefinitions.all.map((attribute) => {
                if (attribute.name === AttributeName.Comment) return null;
                return (
                  <option key={attribute.name} value={attribute.name}>
                    {AttributeUtils.getLabel(attribute.name)}
                  </option>
                );
              })}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          {attribute && (
            <Box flex={1}>
              <AttributeInput
                attribute={attribute}
                value={value}
                setValue={onChangeAttributeValue(i)}
                allowNullValues
              />
            </Box>
          )}

          <IconButton
            variant="ghost"
            size="sm"
            colorPalette="primary"
            disabled={state.length === 1}
            onClick={() => removeItem(i)}
          >
            <Icon as={CloseIcon} />
          </IconButton>
        </HStack>
      ))}
      <HStack>
        <Spacer />
        <ButtonGroup size="sm" colorPalette="primary">
          <Button variant="ghost" onClick={addItem}>
            {t(`map:forecast.AttributeValues.addBtn`)}
          </Button>
          <Button
            flexShrink={0}
            onClick={onSubmit}
            disabled={!validItems.length || !filteredNodes.length}
          >
            {t(`map:forecast.addActionModal.applyBtn`, {
              count: filteredNodes.length,
              total: formatDistanceByUnit(filteredDistance, distanceUnit),
              unit: distanceUnit === DistanceUnit.Imperial ? "mi" : "km",
            })}
          </Button>
        </ButtonGroup>
      </HStack>
    </Stack>
  );
};

export const AddAttributeValuesAction = observer(AddAttributeValuesActionBase);
